import { useProperty, useTranslation } from '@eti/providers';
import { DangerousHtmlContent, Icon } from '@eti/components';
import { css, cx, etiColors, mediaQueries } from '@eti/styles';
import { useLocation } from 'react-router-dom';
import { routes } from '../../constants/routesAndApisConstants';

const wrapperStyles = (isExpanded: boolean) => css`
  align-self: flex-start;
  display: flex;
  flex-direction: column;
  font-size: 0.875rem;
  margin-bottom: ${isExpanded && '1.5rem'};
`;

const iconAndTextWrapperStyles = css`
  display: flex;
`;

const iconStyles = css`
  height: 0.75rem;
  width: 0.75rem;

  @media ${mediaQueries.large.up} {
    height: 1rem;
    width: 1rem;
  }
`;

const contentStyles = css`
  align-items: center;
  font-size: 0.625rem;
  padding: 2px 50px;

  @media ${mediaQueries.large.up} {
    font-size: 0.875rem;
  }

  & > p {
    margin-left: 5px;
    html[dir='rtl'] & {
      margin-left: 0;
      margin-right: 5px;
    }
  }
`;

const textStyles = (color: string) => css`
  color: ${color};

  & a:not([class]),
  & a:hover:not([class]) {
    color: ${color};
  }
`;

const headerStyles = (isExpanded: boolean, isResultPage: boolean) => css`
  align-items: center;
  background-color: #f0fff1;
  border-bottom: ${isResultPage ? `1px dotted ${etiColors.grayScale[7]}` : 'none'};
  display: flex;
  font-size: 0.75rem;
  font-weight: 700;

  @media ${mediaQueries.large.up} {
    font-size: 0.875rem;
  }

  ${isExpanded &&
  css`
    border-bottom: none;
    margin: 15px 28px 9px 20px;

    html[dir='rtl'] & {
      margin-left: 44px;
      margin-right: 22px;
    }

    @media ${mediaQueries.large.up} {
      margin: 15px 64px 9px 24px;
      html[dir='rtl'] & {
        margin-left: 64px;
        margin-right: 44px;
      }
    }
  `};

  padding: 6px 10px;

  & > p {
    margin-left: 5px;
    html[dir='rtl'] & {
      margin-left: 0;
      margin-right: 5px;
    }
  }
`;

interface IconAndTextProps {
  color?: string;
  className: string;
  dataTestId: string;
  id: string;
  text: string;
}
// eslint-disable-next-line react/prop-types
const IconAndText = ({ color, className, dataTestId, id, text }: IconAndTextProps) => {
  const { t } = useTranslation();

  return (
    <div className={cx(iconAndTextWrapperStyles, className)} data-testid={dataTestId}>
      <Icon className={iconStyles} color={color || etiColors.grayScale[8]} id={id} />
      <DangerousHtmlContent
        className={textStyles(color || etiColors.grayScale[10])}
        component="p"
        content={t(text)}
      />
    </div>
  );
};
interface SelfTransferGuaranteeProps {
  className?: string;
  isExpanded: boolean;
  isInTooltip?: boolean;
}
const SelfTransferGuarantee = ({
  className,
  isExpanded,
  isInTooltip,
}: SelfTransferGuaranteeProps) => {
  const location = useLocation();
  const { p } = useProperty();

  const isResultPage = location.pathname.includes(routes.RESULT);

  const showVIIconsAndTextsBaggage = p('SelfTransfer.IconsAndText.Baggage.Enabled');
  const showVIIconsAndTextsTransfer = p('SelfTransfer.IconsAndText.Transfer.Enabled');
  const showVIIconsAndTextsGuarantee = p('SelfTransfer.IconsAndText.Guarantee.Enabled');

  return isExpanded || isInTooltip ? (
    <div className={cx(wrapperStyles(isExpanded), 'selfTransferContentWrapper')}>
      <IconAndText
        className={cx(headerStyles(isExpanded, isResultPage), className)}
        color="#0C7E00"
        dataTestId="self-transfer-header"
        id="self-transfer"
        text="SelfTransfer.Result.Header.Text"
      />
      {(showVIIconsAndTextsBaggage || isInTooltip) && (
        <IconAndText
          className={cx(contentStyles, 'selfTransferContent')}
          dataTestId="self-transfer-content"
          id="luggage-blank"
          text="SelfTransfer.Result.Baggage.Text"
        />
      )}
      {(showVIIconsAndTextsTransfer || isInTooltip) && (
        <IconAndText
          className={cx(contentStyles, 'selfTransferContent')}
          dataTestId="self-transfer-content"
          id="self-transfer"
          text="SelfTransfer.Result.Transfer.Text"
        />
      )}
      {(showVIIconsAndTextsGuarantee || isInTooltip) && (
        <IconAndText
          className={cx(contentStyles, 'selfTransferContent')}
          dataTestId="self-transfer-content"
          id="shield"
          text="SelfTransfer.Result.Guarantee.Text"
        />
      )}
    </div>
  ) : (
    <IconAndText
      className={cx(headerStyles(isExpanded, isResultPage), className)}
      color="#0C7E00"
      dataTestId="self-transfer-header"
      id="self-transfer"
      text="SelfTransfer.Result.Header.Text"
    />
  );
};

export default SelfTransferGuarantee;
